import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Coffee, Wallet, Sparkles, Users, Utensils, MessageCircle, ShieldQuestion, Send, Clock, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import PublicHeader from "../components/PublicHeader";

export default function Info() {
  const [bot, setBot] = useState(null);

  useEffect(() => {
    api.get("/telegram/info").then((r) => setBot(r.data)).catch(() => setBot({ running: false }));
  }, []);

  return (
    <div className="min-h-screen bg-background bg-grain">
      <PublicHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-20 animate-fade-in">
        {/* Hero */}
        <section className="mb-16">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-4">
            Il manifesto
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-none mb-6">
            Pranzo in ufficio,
            <br />
            senza <span className="text-accent">perdere tempo né soldi.</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
            Lo sappiamo tutti com'è la pausa pranzo in ufficio: o si esce a cercare un posto decente,
            o si finisce sempre al solito bar — un toast, un caffè, dieci euro volati e mezz'ora a stomaco mezzo vuoto.
            <br /><br />
            <strong className="text-foreground">Schiscetta</strong> è il modo organizzato in cui i colleghi che amano cucinare portano qualcosa
            di buono per chi non ha tempo, non ha voglia, o semplicemente vuole spendere meno.
          </p>
        </section>

        {/* Bot Telegram quick link */}
        {bot?.running && bot?.deep_link && (
          <section className="mb-16">
            <div className="bg-primary border border-primary rounded-lg p-6 sm:p-8 text-white" data-testid="bot-quick-link-section">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-12 h-12 rounded-full bg-white/15 items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-widest font-semibold opacity-80 mb-1">
                    Bot Telegram attivo
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-2">
                    Apri il bot direttamente
                  </h2>
                  <p className="text-white/85 text-sm sm:text-base mb-4 leading-relaxed">
                    Niente da scaricare in più: usi Telegram, scrivi <code className="px-1.5 py-0.5 bg-white/15 rounded text-xs">/menu</code> e vedi i piatti di oggi.
                  </p>
                  <a
                    href={bot.deep_link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="open-bot-button"
                    className="inline-flex items-center gap-2 px-5 py-3 bg-white text-primary font-medium rounded-md hover:bg-white/90 active:scale-[0.98] transition-all"
                  >
                    Apri @{bot.username} <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Mission concreta */}
        <section className="mb-16">
          <div className="bg-card border border-border rounded-lg p-7 sm:p-10">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold mb-3">
              <Wallet className="w-4 h-4" /> Lo scopo, in chiaro
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-4">
              Risparmiare tempo, risparmiare soldi, mangiare meglio.
            </h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>
                In ogni ufficio ci sono colleghi che cucinano davvero bene — chi prepara la parmigiana della nonna,
                chi la pasta al forno della domenica, chi un'insalatona che salva la giornata d'estate.
                Finora era tutto a voce ("Marì, mi metti da parte una porzione?"), via WhatsApp, su foglietti
                appesi vicino alla macchinetta del caffè.
              </p>
              <p>
                <strong className="text-foreground">Schiscetta mette ordine</strong>: chi prepara pubblica il piatto del giorno con porzioni, prezzo e
                punto di ritiro; chi vuole prenotare lo fa in due tap, dalla scrivania o dal corridoio. Si paga
                tra colleghi, alla buona, ma si sa esattamente quanto e a chi.
              </p>
              <p className="text-sm italic">
                Tradotto: meno cinque euro al giorno al bar, meno code, meno "che si mangia oggi?",
                più tempo per il lavoro vero che ti aspetta sulla scrivania.
              </p>
            </div>
          </div>
        </section>

        {/* Come funziona */}
        <section className="mb-16">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-8">
            Come funziona — quattro passaggi, niente di complicato
          </h2>
          <div className="space-y-5">
            <Step
              icon={Utensils}
              title="1. Il collega che cucina pubblica il piatto"
              text="Nome, foto, prezzo, allergeni, quante porzioni ha disponibili, entro che ora si prenota e dove ritirarlo (es. stanza 142, secondo piano). Tutto qui, niente moduli."
            />
            <Step
              icon={Coffee}
              title="2. Tu apri l'app o il bot Telegram e prenoti"
              text="Vedi il menu del giorno, scegli, fai tap. C'è tempo fino al cutoff (di solito mattina presto, prima dell'inizio della giornata). Se sei tipo distratto: ti arriva un promemoria 30 minuti prima."
            />
            <Step
              icon={Users}
              title="3. All'ora di pranzo, ritiri sul posto"
              text="L'app ti dice esattamente dove andare. Niente giri per l'ufficio a cercare la collega che cucina così bene."
            />
            <Step
              icon={Wallet}
              title="4. Si paga tra colleghi, in contanti o come si è sempre fatto"
              text="In questa prima versione non c'è pagamento online: si fa come al solito, alla mano, con chi ha preparato. L'app tiene traccia di quanto ti è costato: a fine mese sai quanto hai risparmiato rispetto al bar."
            />
          </div>
        </section>

        {/* Telegram details */}
        <section className="mb-16">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-7 sm:p-10">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-semibold mb-3">
              <MessageCircle className="w-4 h-4" /> Tramite Telegram
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight mb-4">
              Anche da Telegram, senza aprire un'altra app.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Se preferisci, prenoti direttamente dal bot. Comodo se sei in riunione o hai due minuti tra
              una pausa e l'altra: scrivi un comando, fai un tap, fatto.
            </p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm mb-5">
              <Cmd cmd="/menu" desc="I piatti di oggi" />
              <Cmd cmd="/ordina" desc="Prenoti in 3 tap" />
              <Cmd cmd="/mieprenotazioni" desc="Cosa hai prenotato" />
              <Cmd cmd="/collega CODICE" desc="Colleghi il tuo profilo" />
            </div>
            {bot?.running && bot?.deep_link && (
              <a
                href={bot.deep_link}
                target="_blank"
                rel="noreferrer"
                data-testid="open-bot-button-inline"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all"
              >
                <Send className="w-4 h-4" /> Apri il bot su Telegram <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </section>

        {/* Numeri / risparmio */}
        <section className="mb-16">
          <div className="grid sm:grid-cols-3 gap-4">
            <Stat icon={Wallet} value="~5 €" label="risparmiati al giorno rispetto al bar" />
            <Stat icon={Clock} value="20 min" label="recuperati dalla pausa pranzo" />
            <Stat icon={Sparkles} value="0" label="messaggi su WhatsApp per organizzarsi" />
          </div>
        </section>

        {/* FAQ pratiche */}
        <section className="mb-16">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">
            <ShieldQuestion className="w-4 h-4" /> Domande pratiche
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-6">
            Le cose che ti starai chiedendo.
          </h2>
          <div className="space-y-5">
            <Faq q="Devo pagare l'iscrizione o un abbonamento?">
              Per i singoli utenti l'uso è gratuito: lo strumento serve solo a organizzare le prenotazioni tra colleghi.
              Il costo del pasto lo decide chi cucina e lo paghi direttamente a lui o lei.
              L'attivazione della piattaforma per un nuovo ufficio è invece a pagamento — scrivici dalla pagina
              <Link to="/contatti" className="text-primary hover:underline mx-1">Contatti</Link>
              per un preventivo.
            </Faq>
            <Faq q="Sono celiaco / vegetariano / ho intolleranze: posso usarlo?">
              Sì. Ogni piatto riporta ingredienti e allergeni dichiarati da chi lo prepara.
              Se hai dubbi specifici, scrivi al collega direttamente: di solito è due stanze più in là.
            </Faq>
            <Faq q="Posso ordinare più porzioni? Anche per un collega?">
              Sì, fino a 5 porzioni per prenotazione. Utile se in stanza siete in più e si fa un ordine unico.
            </Faq>
            <Faq q="E se prenoto e poi mi salta il pranzo all'improvviso?">
              Puoi annullare la prenotazione fino all'orario di cutoff fissato dal preparatore.
              Dopo, la pasta è già sul fuoco e il collega ha contato anche su di te.
            </Faq>
            <Faq q="Funziona anche per la merenda di metà mattina o pomeriggio?">
              In teoria sì: chi prepara può pubblicare quello che vuole, a qualsiasi ora. Va benissimo
              anche per una torta o una pizza rustica fatta in casa.
            </Faq>
            <Faq q="I miei dati come vengono trattati?">
              Solo nome, email aziendale e prenotazioni effettuate. Niente esterni, niente pubblicità,
              niente terze parti. Ogni ufficio ha i propri dati isolati e protetti.
            </Faq>
            <Faq q="Si può attivare nel mio ufficio?">
              Sì. Schiscetta è multi-ufficio: ogni sede ha il proprio menu, i propri utenti e il proprio
              bot Telegram, completamente separati. Scrivici dalla pagina
              <Link to="/contatti" className="text-primary hover:underline mx-1">Contatti</Link>
              per attivare la tua.
            </Faq>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-8 border-t border-border">
          <p className="text-muted-foreground mb-5">Pronto a smettere di pagare 8 € per un panino del bar?</p>
          <Link
            to="/register"
            data-testid="info-cta-register"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary text-white font-medium rounded-md hover:bg-[#436343] active:scale-[0.98] transition-all"
          >
            Crea il tuo account <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-muted-foreground mt-4">
            Oppure <Link to="/login" className="text-primary hover:underline">accedi</Link> se ce l'hai già.
          </p>
        </section>
      </main>

      <footer className="border-t border-border py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center text-xs text-muted-foreground">
          Schiscetta · piattaforma interna per uffici pubblici e privati · 2026
        </div>
      </footer>
    </div>
  );
}

function Step({ icon: Icon, title, text }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 sm:p-6 flex items-start gap-4">
      <div className="flex-shrink-0 w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-display font-medium text-lg mb-1">{title}</div>
        <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function Cmd({ cmd, desc }) {
  return (
    <div className="flex items-center gap-2">
      <code className="px-2 py-1 bg-white border border-border rounded text-xs font-mono text-primary">{cmd}</code>
      <span className="text-muted-foreground">{desc}</span>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="bg-card border border-border rounded-lg p-5 text-center">
      <Icon className="w-5 h-5 text-primary mx-auto mb-2" />
      <div className="font-display text-3xl font-semibold tracking-tight">{value}</div>
      <div className="text-xs text-muted-foreground mt-1 leading-snug">{label}</div>
    </div>
  );
}

function Faq({ q, children }) {
  return (
    <details className="bg-card border border-border rounded-lg group" data-testid="faq-item">
      <summary className="cursor-pointer p-5 font-medium flex items-center justify-between gap-3 list-none">
        <span>{q}</span>
        <span className="text-muted-foreground group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="px-5 pb-5 text-sm text-muted-foreground leading-relaxed -mt-1">
        {children}
      </div>
    </details>
  );
}
